import React, { useMemo } from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import XCloseIcon from "assets/icons/HeroIcons/XCloseIcon";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { closeReportVersionDataModalAction } from "store/modals/reducer.actions";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import NoRecordFound from "view/components/NoRecordFound";
import { Table } from "Components/ReportVersionData";
import { ReportVersionDataTable } from "hooks/useTable";

const style: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "10px",
    backgroundColor: "#ffff",
};


const parseGroups = (data: string): any => {
    const groups: { group: string, answer: string }[] = [];

    // Trim the entire input string
    data = data.trim();

    // Remove quotation marks at the start and end if they exist
    if (data.startsWith('"') && data.endsWith('"')) {
        data = data.slice(1, -1);
    }

    // Regular expression to match groups and their values
    const regex = /group#\d+:\s*\[[^\]]+\]|group#\d+:\s*[^,]+/g;
    const entries = data.match(regex);

    if (entries) {
        entries.forEach(entry => {
            const [key, value] = entry.split(':').map(part => part.trim());

            let answer: string;

            // Check if the value is an array in string format
            if (value.startsWith('[') && value.endsWith(']')) {
                // Remove the brackets and join the items by comma
                answer = value.slice(1, -1).split(',').map(item => item.trim()).join(', ');
            } else {
                answer = value;
            }

            groups.push({ group: key, answer: answer });
        });

        return groups
    }
    else {
        return data;
    }
};

const parseNamedValue = (data: string) => {
    let normalizedData = data.trim();

    if (normalizedData.startsWith('"') && normalizedData.endsWith('"')) {
        normalizedData = normalizedData.slice(1, -1);
    }

    if (!normalizedData) return null;

    const jsonStart = normalizedData.indexOf('{');
    const separatorIndex = jsonStart >= 0
        ? normalizedData.lastIndexOf(':', jsonStart)
        : normalizedData.indexOf(':');
    const name = separatorIndex >= 0
        ? normalizedData
            .slice(0, separatorIndex)
            .replace(/^"+|"+$/g, '')
            .trim()
        : '';
    const rawValue = jsonStart >= 0
        ? normalizedData.slice(jsonStart).trim()
        : separatorIndex >= 0
            ? normalizedData.slice(separatorIndex + 1).trim()
            : normalizedData;

    if (!rawValue) return null;

    const jsonValue = rawValue.replace(/""/g, '"');
    let value = jsonValue;
    let isGeography = false;

    try {
        const parsedValue = JSON.parse(jsonValue);
        const geographyTypes = [
            "Point",
            "MultiPoint",
            "LineString",
            "MultiLineString",
            "Polygon",
            "MultiPolygon",
            "GeometryCollection",
            "Feature",
            "FeatureCollection",
        ];

        isGeography = geographyTypes.includes(parsedValue?.type);
        value = JSON.stringify(parsedValue, null, 2);
    } catch {
        // Keep the original value when it is not valid JSON.
    }

    return { name, value, isGeography };
};

interface ReportVersionDataModalProps { }

export const ReportVersionDataModal: React.FC<
    ReportVersionDataModalProps
> = () => {

    const { reportVersionDataModal } = useSelector(
        (state: RootState) => state.modals
    );

    const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

    const groupsData = useMemo(() => {
        if (reportVersionDataModal?.data?.group) {
            return parseGroups(reportVersionDataModal?.data.group)
        }
        return []
    }, [reportVersionDataModal?.data])

    const namedValue = useMemo(() => {
        return typeof groupsData === "string" ? parseNamedValue(groupsData) : null;
    }, [groupsData]);

    const {
        flexRender,
        columnVisibility,
        columnsConfig,
        table,
        setColumnsConfig,
        setColumnVisibility,
        setSorting,
        sorting,
    } = ReportVersionDataTable(groupsData);

    const handleClose = () => {
        dispatch(closeReportVersionDataModalAction());
    };

    return (
        <Modal
            open={reportVersionDataModal?.isOpen}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            disableAutoFocus={true}
            aria-describedby="modal-modal-description"
            className="border-none"
        >
            <Box sx={style} className="dark:bg-secondaryLight ">
                <div className="flex w-[700px] max-w-[calc(100vw-32px)] flex-col items-start justify-start rounded-lg">
                    <div
                        className="relative flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-4 rounded-lg dark:bg-secondaryLight bg-bgWhite"
                        style={{
                            boxShadow:
                                "0px 2px 8px 0 rgba(2,13,36,0.1), 0px 2px 24px 0 rgba(2,13,36,0.08)",
                        }}
                    >
                        <div className="w-full flex justify-end pt-3 px-3">
                            <button onClick={handleClose} type="button">
                                <XCloseIcon width={24} height={24} viewBox="0 0 24 24" />
                            </button>
                        </div>

                        <div className="pb-6 px-6 flex flex-col items-start self-stretch justify-start ">
                            <div className="w-full flex justify-between items-center mt-[-10px] mb-4 ml-2">
                                <p className="break-words pr-4 text-left text-xl font-semibold text-textMid dark:text-textMain">
                                    Column: {reportVersionDataModal?.data?.question}
                                </p>
                            </div>

                            <div className="flex flex-col items-start self-stretch justify-start overflow-x-hidden overflow-y-auto px-1 max-h-[60vh] w-full dark:bg-secondaryLight bg-bgWhite">
                                {namedValue?.isGeography ? (
                                    <table className="w-full table-fixed border-collapse text-left text-sm">
                                        <thead>
                                            <tr className="border border-lineMid bg-bgLightGrey dark:bg-secondary">
                                                <th className="w-1/3 border-r border-lineMid px-4 py-3 font-semibold text-textMid dark:text-textMain">
                                                    Name
                                                </th>
                                                <th className="w-2/3 px-4 py-3 font-semibold text-textMid dark:text-textMain">
                                                    Value
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border border-t-0 border-lineMid">
                                                <td className="border-r border-lineMid px-4 py-3 align-top text-textMid dark:text-textMain">
                                                    {namedValue.name || "—"}
                                                </td>
                                                <td className="px-4 py-3 align-top text-left text-textMid dark:text-textMain">
                                                    <pre className="m-0 max-h-[45vh] overflow-auto whitespace-pre-wrap break-words text-left font-sans">{namedValue.value.trim()}</pre>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                ) : groupsData?.length === 0 ? (
                                    <NoRecordFound />
                                ) : typeof groupsData === "string" ?
                                    <div className="w-full px-2 py-3 text-sm text-textMid dark:text-textMain">
                                        <p className="whitespace-pre-wrap break-words leading-6">
                                            {groupsData.replace(/^"|"$/g, '')}
                                        </p>
                                    </div>
                                    :
                                    <Table
                                        flexRender={flexRender}
                                        table={table}
                                        columns={columnsConfig}
                                        columnVisibility={columnVisibility}
                                        setSorting={setSorting}
                                        sorting={sorting}
                                    />
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </Box>
        </Modal>
    );
};
